import { QueryKeys } from "@/constants/Query";
import { useApi } from "@/hooks/useApi";
import axiosInstance from "@/lib/axios";
import type { Medication } from "@curai/js-fhir-tools";

export enum SeverityLevel {
  MINOR = 1,
  MODERATE = 2,
  MAJOR = 3,
}

export const SEVERITY_LEVEL_LABELS = {
  [SeverityLevel.MINOR]: "Minor",
  [SeverityLevel.MODERATE]: "Moderate",
  [SeverityLevel.MAJOR]: "Major",
};

export enum AllergyReactionType {
  ALLERGY = 1,
  ADVERSE_REACTION = 2,
}

export const ALLERGY_REACTION_TYPE_LABELS = {
  [AllergyReactionType.ALLERGY]: "Allergy",
  [AllergyReactionType.ADVERSE_REACTION]: "Adverse Reaction",
};

interface InteractionCheckingParams {
  patientId: string;
  medicationRequestId?: string;
  dispensableDrugId?: string;
  enabled?: boolean;
}

interface DrugDrugInteraction {
  clinicalDescription: string;
  isProspective: boolean;
  severityLevel: SeverityLevel;
  drugs: Medication[];
}

interface DrugAllergyInteraction {
  message: string;
  reaction: string;
  reactionType: AllergyReactionType;
}

export interface InteractionCheckingResponse {
  drugDrugInteractions: DrugDrugInteraction[];
  drugAllergyInteractions: DrugAllergyInteraction[];
}

export const useInteractionChecking = ({
  patientId,
  medicationRequestId,
  dispensableDrugId,
  enabled = true,
}: InteractionCheckingParams) => {
  return useApi<InteractionCheckingResponse>({
    queryOptions: {
      enabled:
        !!patientId && !!medicationRequestId && !!dispensableDrugId && enabled,
      queryFn: async () => {
        const response = await axiosInstance.get<InteractionCheckingResponse>(
          `/rx/interactions/patients/${patientId}/precheck`,
          {
            params: {
              medicationRequestId,
              dispensableDrugId,
            },
          },
        );

        return response.data;
      },
      queryKey: QueryKeys.Patient.Chart.Prescriptions.INTERACTION_CHECKING({
        patientId,
        medicationRequestId: medicationRequestId || "",
        dispensableDrugId: dispensableDrugId || "",
      }),
    },
  });
};
